import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import Loader from "../common/Loader.jsx";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete } from "../common/permissionUtils";
import DOMPurify from "dompurify";
import "./payment.css";
import * as paymentApi from "./payment-api";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";
import { FaCheckCircle, FaDownload } from "react-icons/fa";

const sanitize = (input) => {
  if (typeof input !== "string") return input;
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
const decodeHtmlEntity = (str) => {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

// Formats an amount with the given currency symbol (falls back to ₹ only
// when the API hasn't provided one for this record).
const fmtAmount = (v, symbol) =>
  v == null || v === ""
    ? "—"
    : `${symbol || ""}${num(v).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-red-50 text-red-700 border-red-200",
  partially: "bg-amber-50 text-amber-700 border-amber-200",
  "partially paid": "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-gray-100 text-gray-700 border-gray-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  default: "bg-gray-50 text-gray-700 border-gray-200",
};

const pickPayment = (body) =>
  body?.payment ?? body?.data?.payment ?? body?.data ?? body ?? {};

const pickInvoices = (payment, body) => {
  const candidates = [
    payment?.invoice_payment,
    payment?.invoices,
    payment?.invoice,
    body?.invoice_payment,
    body?.invoices,
  ];
  for (const c of candidates) if (Array.isArray(c) && c.length > 0) return c;
  return [];
};

const pickAttachments = (payment, body) => {
  const candidates = [
    payment?.attachments,
    payment?.attachment,
    payment?.files,
    body?.attachments,
  ];
  for (const c of candidates) if (Array.isArray(c)) return c;
  if (typeof payment?.attachments === "string" && payment.attachments) {
    return [{ name: "attachment", url: payment.attachments }];
  }
  return [];
};

const PaymentHistoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Payment");
  const hasDeletePermission = canDelete(rolePermissionList, "Payment");

  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        console.log("🔍 Fetching payment history for ID:", id);
        const response = await paymentApi.getPaymentHistoryViewApi(id);
        console.log("📦 Payment history response:", response);
        const body = response?.data ?? {};

        if (cancelled) return;

        const p = pickPayment(body);
        console.log("💳 Payment details:", p);
        setPayment(p);
        setInvoices(pickInvoices(p, body));
        setAttachments(pickAttachments(p, body));
      } catch (error) {
        if (!cancelled) {
          console.error("❌ Error fetching payment:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#F5F6F8",
          width: "100%"
        }}
      >
        <Loader height="100vh" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#F5F6F8" }}
      >
        <div style={{ color: "#8790A1", fontSize: "15px" }}>
          Payment not found
        </div>
      </div>
    );
  }

  const paymentId = sanitizeInput(
    payment.payment_no || payment.payment_id || payment.id || `#${id}`
  );
  const customerName = sanitizeInput(
    payment.customer_name || payment.customer || "Customer"
  );
  const customerInitials = (customerName || "?").slice(0, 2).toUpperCase();
  const paymentDate = sanitizeInput(payment.payment_date || payment.date || "");
  const createdAt = sanitizeInput(
    payment.created || payment.created_at || ""
  );
  const method = sanitizeInput(
    payment.payment_method ||
      payment.payment_mode ||
      payment.payment_type ||
      "—"
  );
  const amount =
    payment.amount ?? payment.payment_amount ?? payment.total_amount;
  const paymentCurrencySymbol = decodeHtmlEntity(sanitizeInput(payment.currency_symbol || ""));
  const orgCurrencySymbol = decodeHtmlEntity(sanitizeInput(payment.org_currency_symbol || ""));
  const organizationCurrencyId = payment.organization_currency_id ?? null;
  const status = sanitizeInput(payment.status || payment.payment_status || "");
  const statusKey = status.toLowerCase();
  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.default;
  const comment = sanitizeInput(
    payment.comment || payment.remarks || payment.note || ""
  );
  const balanceChecked = !!(payment.balance_checked ?? payment.is_balance_amt);

  const invoiceTotal = invoices.reduce(
    (s, r) => s + num(r.amount ?? r.paid ?? r.payment_amount ?? 0),
    0
  );

  return (
    <div className="payment-history-view-page" style={{ backgroundColor: "#F5F6F8", minHeight: "100vh" }}>
      {/* Top bar */}
      <div
        className="payment-history-top-bar"
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #D5DDE7",
          padding: "16px 32px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="#1E1E1E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "19px",
                  fontWeight: "600",
                }}
              >
                {customerInitials}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: "600",
                    color: "#1E1E1E",
                  }}
                >
                  {paymentId}
                </div>
                <div style={{ fontSize: "13px", color: "#8790A1" }}>
                  {customerName}
                  {paymentDate && <span> · {paymentDate}</span>}
                </div>
              </div>
            </div>
          </div>

          {status && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 16px",
                borderRadius: "100px",
                fontSize: "13px",
                fontWeight: "500",
                border: "1px solid",
              }}
              className={statusStyle}
            >
              {statusKey === "paid" && <FaCheckCircle size={12} />}
              {status}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          padding: "8px 16px",
          maxWidth: "1440px",
          margin: "0 auto",
        }}
      >
        {/* Headline summary */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "16px",
            border: "1px solid #D5DDE7",
            padding: "16px",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#8790A1",
                  textTransform: "uppercase",
                  marginBottom: "8px",
                }}
              >
                Amount Paid
              </div>
              <div
                style={{
                  fontSize: "29px",
                  fontWeight: "700",
                  color: "#21272C",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtAmount(amount, paymentCurrencySymbol)}
              </div>
              <div style={{ fontSize: "13px", color: "#8790A1", marginTop: "8px" }}>
                via <span style={{ fontWeight: "500", color: "#21272C" }}>{method}</span>
                {createdAt && <span> · {createdAt}</span>}
              </div>
            </div>
            <div
              style={{
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                width: "64px",
                height: "64px",
                borderRadius: "16px",
                backgroundColor: "#ECFDF5",
                color: "#059669",
              }}
              className="d-none d-md-flex"
            >
              💰
            </div>
          </div>

          {balanceChecked && (
            <div
              style={{
                marginTop: "16px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor: "#DBEAFE",
                color: "#1E40AF",
                border: "1px solid #BFDBFE",
              }}
            >
              <FaCheckCircle size={12} /> Customer balance applied
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "8px", marginBottom: "8px" }}>
          {/* Left — Payment + method details */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Payment details */}
            <PaymentCard title="Payment Details">
              <DetailRow label="Payment ID" value={paymentId} mono />
              <DetailRow label="Payment Date" value={paymentDate || "—"} />
              <DetailRow label="Created" value={createdAt || "—"} />
              <DetailRow label="Method" value={method} />
              <DetailRow label="Amount" value={fmtAmount(amount, paymentCurrencySymbol)} bold />
            </PaymentCard>

            {/* Method-specific details */}
            {(payment.cheque_number ||
              payment.account_number ||
              payment.net_banking_id ||
              payment.account_name) && (
              <PaymentCard title="Method Details">
                {payment.cheque_number && (
                  <DetailRow
                    label="Cheque Number"
                    value={sanitizeInput(payment.cheque_number)}
                    mono
                  />
                )}
                {payment.account_number && (
                  <DetailRow
                    label="Account Number"
                    value={sanitizeInput(payment.account_number)}
                    mono
                  />
                )}
                {payment.account_name && (
                  <DetailRow
                    label="Account Name"
                    value={sanitizeInput(payment.account_name)}
                  />
                )}
                {payment.net_banking_id && (
                  <DetailRow
                    label="Net Banking ID"
                    value={sanitizeInput(payment.net_banking_id)}
                    mono
                  />
                )}
              </PaymentCard>
            )}
          </div>

          {/* Right — Customer + attachments + comment */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <PaymentCard title="Customer">
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: "#1E40AF",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  {customerInitials}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#21272C",
                    }}
                  >
                    {customerName}
                  </div>
                  {payment.customer_id && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/payment/view/${payment.customer_id}`)
                      }
                      style={{
                        fontSize: "11px",
                        color: "#3D4AF5",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      View customer history
                    </button>
                  )}
                </div>
              </div>
            </PaymentCard>

            {comment && (
              <PaymentCard title="Comment">
                <p
                  style={{
                    fontSize: "13px",
                    color: "#21272C",
                    whiteSpace: "pre-wrap",
                    margin: 0,
                  }}
                >
                  {comment}
                </p>
              </PaymentCard>
            )}

            <PaymentCard title={`Attachments (${attachments.length})`}>
              {attachments.length === 0 ? (
                <div style={{ fontSize: "13px", color: "#8790A1" }}>
                  No attachments.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "150px", overflowY: "auto", paddingRight: "4px" }}>
                  {attachments.map((a, idx) => {
                    const name =
                      a.name ||
                      a.file_name ||
                      `attachment-${idx + 1}`;
                    const url =
                      a.url || a.file_path || a.file_url || a.base64 || a;
                    const size = a.size
                      ? `${(a.size / 1024).toFixed(1)} KB`
                      : "";
                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          const isPdf = name.toLowerCase().endsWith(".pdf");
                          const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(name.toLowerCase());
                          if (isPdf || isImage) {
                            setPreviewFile({ name, url, isPdf, isImage });
                            setShowPreview(true);
                          } else {
                            window.open(url, "_blank");
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px",
                          backgroundColor: "#F9FAFB",
                          border: "1px solid #EBEBEB",
                          borderRadius: "8px",
                          cursor: "pointer",
                          textDecoration: "none",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "#F0F0F0")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "#F9FAFB")
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "6px",
                              backgroundColor: "#059669",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                            }}
                          >
                            {(name.split(".").pop() || "?")
                              .slice(0, 3)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "500",
                                color: "#21272C",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {name}
                            </div>
                            {size && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#8790A1",
                                }}
                              >
                                {size}
                              </div>
                            )}
                          </div>
                        </div>
                        <FaDownload
                          size={12}
                          style={{
                            color: "#8790A1",
                            flexShrink: 0,
                            cursor: "pointer",
                            transition: "color 0.2s"
                          }}
                          title="Download file"
                          onMouseEnter={(e) => (e.currentTarget.style.color = "#3D4AF5")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "#8790A1")}
                          onClick={(e) => {
                            e.stopPropagation();
                            fetch(url)
                              .then((r) => r.blob())
                              .then((blob) => {
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = blobUrl;
                                link.download = name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(blobUrl);
                              })
                              .catch(() => {
                                window.open(url, "_blank");
                              });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </PaymentCard>
          </div>
        </div>

        {/* Allocated Invoices outside grid (takes full width) */}
        <PaymentCard title={`Allocated Invoices (${invoices.length})`}>
          {invoices.length === 0 ? (
            <div
              style={{
                padding: "32px",
                textAlign: "center",
                color: "#8790A1",
                fontSize: "13px",
              }}
            >
              No invoice allocations recorded for this payment.
            </div>
          ) : (
            <div style={{ overflowX: "auto", marginLeft: "-24px", marginRight: "-24px" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "1px solid #D5DDE7" }}>
                    <th
                      style={{
                        padding: "10px 24px",
                        textAlign: "left",
                        fontSize: "12px",
                        color: "#8790A1",
                        fontWeight: "500",
                        textTransform: "uppercase",
                      }}
                    >
                      Invoice
                    </th>
                    <th
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontSize: "12px",
                        color: "#8790A1",
                        fontWeight: "500",
                        textTransform: "uppercase",
                      }}
                    >
                      Total
                    </th>
                    <th
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontSize: "12px",
                        color: "#8790A1",
                        fontWeight: "500",
                        textTransform: "uppercase",
                      }}
                    >
                      Paid
                    </th>
                    <th
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontSize: "12px",
                        color: "#8790A1",
                        fontWeight: "500",
                        textTransform: "uppercase",
                      }}
                    >
                      TDS
                    </th>
                    <th
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontSize: "12px",
                        color: "#8790A1",
                        fontWeight: "500",
                        textTransform: "uppercase",
                      }}
                    >
                      Outstanding
                    </th>
                    {orgCurrencySymbol && (
                      <th
                        style={{
                          padding: "10px 24px",
                          textAlign: "right",
                          fontSize: "12px",
                          color: "#8790A1",
                          fontWeight: "500",
                          textTransform: "uppercase",
                        }}
                      >
                        Exchange Rate
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, idx) => {
                    const invoiceNo = sanitizeInput(
                      inv.invoice_no ||
                        inv.invoice_number ||
                        inv.invoice ||
                        `#${inv.id ?? idx}`
                    );
                    const total =
                      inv.total_amount ??'0';
                    const paid =
                      inv.amount ?? '0';
                       const tds =
                      inv.tds_amount_bc ??'0';
                    const out = (parseFloat(total) - parseFloat(paid) - parseFloat(tds)).toFixed(2);
                      //inv.pending_amount ?? inv.balance ?? inv.remaining;
                    const invCurrencySymbol = decodeHtmlEntity(sanitizeInput(inv.currency_symbol || ""));
                    const showExchangeRate =
                      orgCurrencySymbol &&
                      organizationCurrencyId &&
                      inv.customer_currency_id &&
                      organizationCurrencyId !== inv.customer_currency_id &&
                      inv.exchange_rate;
                    const convertedAmount =
                      inv.converted_amount != null && inv.converted_amount !== ""
                        ? num(inv.converted_amount)
                        : num(paid) * num(inv.exchange_rate);

                    return (
                      <tr
                        key={inv.id ?? idx}
                        style={{
                          borderBottom: "1px solid #EBEBEB",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#F9FAFB")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "white")
                        }
                      >
                        <td
                          style={{
                            padding: "12px 24px",
                            fontSize: "13px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#21272C",
                            }}
                          >
                            {invoiceNo}
                          </div>
                          {inv.invoice_date && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#8790A1",
                              }}
                            >
                              {sanitizeInput(inv.invoice_date)}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            color: "#21272C",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtAmount(total, invCurrencySymbol)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#17B65B",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtAmount(paid, invCurrencySymbol)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            color: "#21272C",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {tds == null || tds === ""
                            ? "—"
                            : `${num(tds).toFixed(2)}`}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            fontWeight: "500",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {out == null || out === "" ? (
                            <span style={{ color: "#999" }}>—</span>
                          ) : (
                            <span
                              style={{
                                color:
                                  num(out) > 0 ? "#EF4444" : "#21272C",
                              }}
                            >
                              {fmtAmount(out, invCurrencySymbol)}
                            </span>
                          )}
                        </td>
                        {orgCurrencySymbol && (
                          <td
                            style={{
                              padding: "12px 24px",
                              fontSize: "13px",
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {showExchangeRate ? (
                              <>
                                <div style={{ color: "#21272C" }}>
                                  × {num(inv.exchange_rate)}
                                </div>
                                <div style={{ fontSize: "11px", color: "#8790A1" }}>
                                  {fmtAmount(convertedAmount, orgCurrencySymbol)}
                                </div>
                              </>
                            ) : (
                              <span style={{ color: "#999" }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      borderTop: "1px solid #D5DDE7",
                      backgroundColor: "#F9FAFB",
                    }}
                  >
                    <td
                      colSpan={2}
                      style={{
                        padding: "10px 24px",
                        fontSize: "11px",
                        color: "#8790A1",
                        textTransform: "uppercase",
                      }}
                    >
                      Total allocated
                    </td>
                    <td
                      colSpan={orgCurrencySymbol ? 4 : 3}
                      style={{
                        padding: "10px 24px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#21272C",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtAmount(invoiceTotal, paymentCurrencySymbol)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </PaymentCard>
      {/* Attachment Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "16px", fontWeight: "600" }}>{previewFile?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-3" style={{ backgroundColor: "#F9FAFB" }}>
          {previewFile?.isImage && (
            <img
              src={previewFile.url}
              alt={previewFile.name}
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "8px" }}
            />
          )}
          {previewFile?.isPdf && (
            <iframe
              src={previewFile.url}
              title={previewFile.name}
              width="100%"
              height="550px"
              style={{ border: "none", borderRadius: "8px" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowPreview(false)}>
            Close
          </Button>
           <Button
            variant="primary"
            onClick={() => {
              if (!previewFile) return;
              fetch(previewFile.url)
                .then((r) => r.blob())
                .then((blob) => {
                  const blobUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = previewFile.name;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(blobUrl);
                })
                .catch(() => {
                  window.open(previewFile.url, "_blank");
                });
            }}
          >
            Download File
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  </div>
  );
};

function PaymentCard({ title, children }) {
  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "16px",
        border: "1px solid #D5DDE7",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "16px 24px",
          borderBottom: "1px solid #EBEBEB",
        }}
      >
        <h3
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#21272C",
            margin: 0,
          }}
        >
          {title}
        </h3>
      </div>
      <div style={{ padding: "16px 24px" }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value, mono, bold }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #F0F0F0",
      }}
    >
      <div style={{ fontSize: "12px", color: "#8790A1" }}>{label}</div>
      <div
        style={{
          fontSize: "13px",
          color: "#21272C",
          fontFamily: mono ? "monospace" : "inherit",
          fontWeight: bold ? "600" : "500",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default PaymentHistoryView;
